import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api, { formatDateTime } from '../lib/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import {
  Plus,
  Scroll,
  PencilSimple,
  Trash,
  CheckCircle,
  XCircle,
  Phone
} from '@phosphor-icons/react';

export default function Scripts() {
  const navigate = useNavigate();
  const [scripts, setScripts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteId, setDeleteId] = useState(null);

  useEffect(() => {
    loadScripts();
  }, []);

  const loadScripts = async () => {
    try {
      const response = await api.get('/scripts');
      setScripts(response.data.scripts);
    } catch (error) {
      toast.error('Failed to load scripts');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await api.delete(`/scripts/${deleteId}`);
      toast.success('Script deleted');
      setDeleteId(null);
      loadScripts();
    } catch (error) {
      toast.error('Failed to delete script');
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-screen">
        <div className="text-zinc-400">Loading scripts...</div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="scripts-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-medium text-zinc-50 tracking-tight">Call Scripts</h1>
          <p className="text-zinc-400 mt-1">Configure AI conversation scripts for your calls</p>
        </div>
        <Link to="/dashboard/scripts/new" className="btn-primary flex items-center gap-2" data-testid="create-script-btn">
          <Plus size={18} /> Create Script
        </Link>
      </div>

      {/* Scripts Grid */}
      {scripts.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {scripts.map((script) => (
            <div key={script.id} className="card group hover:border-zinc-700 transition-colors" data-testid={`script-${script.id}`}>
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-zinc-800 rounded-sm flex items-center justify-center">
                    <Scroll size={20} className="text-zinc-400" />
                  </div>
                  <div>
                    <h3 className="font-medium text-zinc-50">{script.name}</h3>
                    <div className="flex items-center gap-2 mt-1">
                      {script.isActive ? (
                        <span className="badge badge-success flex items-center gap-1">
                          <CheckCircle size={12} /> Active
                        </span>
                      ) : (
                        <span className="badge badge-neutral flex items-center gap-1">
                          <XCircle size={12} /> Inactive
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {script.description && (
                <p className="text-sm text-zinc-400 mb-4 line-clamp-2">{script.description}</p>
              )}

              <div className="flex items-center gap-4 text-xs text-zinc-500 mb-4">
                <span>{script.qualifyingQuestions?.length || 0} questions</span>
                <span>{script.objectionHandlers?.length || 0} objection handlers</span>
                <span>{script.maxCallDurationMinutes || 10} min max</span>
              </div>

              <div className="flex items-center gap-2 pt-3 border-t border-zinc-800 opacity-0 group-hover:opacity-100 transition-opacity">
                <Link
                  to={`/dashboard/scripts/${script.id}`}
                  className="btn-secondary flex-1 text-sm py-1.5 flex items-center justify-center gap-1"
                >
                  <PencilSimple size={14} /> Edit
                </Link>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setDeleteId(script.id);
                  }}
                  className="btn-secondary text-sm py-1.5 px-3 text-red-400 hover:text-red-300"
                >
                  <Trash size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="card text-center py-16">
          <Scroll size={48} className="mx-auto mb-4 text-zinc-600" />
          <h3 className="text-lg font-medium text-zinc-50 mb-2">No scripts yet</h3>
          <p className="text-zinc-400 mb-6">
            Create your first call script to get started with AI-powered conversations
          </p>
          <Link to="/dashboard/scripts/new" className="btn-primary inline-flex items-center gap-2">
            <Plus size={18} /> Create Your First Script
          </Link>
        </div>
      )}

      {/* Delete Confirmation */}
      <Dialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-zinc-50">Delete Script</DialogTitle>
          </DialogHeader>
          <p className="text-zinc-400">
            Are you sure you want to delete this script? This action cannot be undone.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteId(null)} className="border-zinc-700">
              Cancel
            </Button>
            <Button onClick={handleDelete} className="bg-red-600 hover:bg-red-500">
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
